import { useQuery } from "@tanstack/react-query";
import {
  fetchStudentBalanceStatements,
  type BalanceStatementRequest,
} from "@/features/balance/utils/fetch-student-statements";
import {
  STATEMENT_SUMMARY_PAGE_SIZE,
  STUDENT_STATEMENT_PAGE_SIZE,
} from "@/features/balance/utils/user-balance-statements-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { QueryKeys } from "@/lib/types";

export { STATEMENT_SUMMARY_PAGE_SIZE, STUDENT_STATEMENT_PAGE_SIZE };

interface UseStudentBalanceStatementsProps {
  studentId: number;
  request: BalanceStatementRequest;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

export const useStudentBalanceStatements = ({
  studentId,
  request,
  page = 1,
  pageSize = STUDENT_STATEMENT_PAGE_SIZE,
  enabled = true,
}: UseStudentBalanceStatementsProps) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  const { data, isError, isPending, isFetching, isPlaceholderData, refetch } =
    useQuery({
      queryKey: [
        QueryKeys.Balance,
        "student-statements",
        studentId,
        request,
        page,
        pageSize,
      ],
      queryFn: async () => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }
        return fetchStudentBalanceStatements({
          accessToken,
          tenantId,
          studentId,
          request,
          page,
          pageSize,
        });
      },
      enabled: enabled !== false && !!accessToken && studentId > 0,
      placeholderData: (previousData) => previousData,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  return {
    data,
    isError,
    isPending,
    isFetching,
    isPlaceholderData,
    refetch,
  };
};
